// «Было — стало» записи журнала словами (contracts/ui.md, «Журнал действий») — для экрана журнала
// и выгрузки в Excel (сценарий 4.3, FR-033): короткие значения — «контролёр → ведущий», списки —
// «добавлено: …, убрано: …»; роли, состояния, области, даты и время — словами и по поясу
// установки, номера пользователей — ФИО, если оно известно. Перенесено из apps/web/src/app/changes.ts.
import { jobCatalog } from '../jobs/catalog.ts';
import { formatOperation } from '../kernel/common.ts';
import { jobStateLabels } from '../observability/job.ts';
import { roleInfo, roles } from '../rights/roles.ts';
import type { Role } from '../rights/roles.ts';
import { isOperation } from '../rights/matrix.ts';
import type { AuditEntry } from './entry.ts';
import {
  areaKindLabels,
  auditOperationLabel,
  auditResultLabels,
  entityTypeLabel,
  fieldLabel,
  operationLabels,
  purposeLabels,
  reasonLabels,
  substitutionStateLabels,
  supportGrantStateLabels,
  userIdFields,
  userStateLabels,
} from './labels.ts';

/**
 * ФИО по номеру пользователя — из текущих записей пользователей (FR-033): поле names записи
 * журнала (AuditEntry.names) или словарь, собранный вызывающим.
 */
export type PeopleNames = ReadonlyMap<string, string> | Readonly<Record<string, string>>;

function nameIn(names: PeopleNames | undefined, id: string): string | undefined {
  if (!names) return undefined;
  if (names instanceof Map) return (names as ReadonlyMap<string, string>).get(id);
  return Object.hasOwn(names, id) ? (names as Readonly<Record<string, string>>)[id] : undefined;
}

export interface Change {
  key: string;
  label: string;
  /** set — было и стало; list — добавлено и убрано; value — только одно значение (создание). */
  kind: 'set' | 'list' | 'value';
  before?: string;
  after?: string;
  added?: string[];
  removed?: string[];
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const AREA_KEY = /^(installation|legal_entity|object|document_type|document)(?::(.+))?$/;

const formatters = new Map<string, Intl.DateTimeFormat>();

/** «14.10.2026 15:42» по часовому поясу установки. */
export function formatAuditTime(iso: string, timeZone: string): string {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('ru-RU', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, formatter);
  }
  const p: Record<string, string> = {};
  for (const part of formatter.formatToParts(new Date(iso))) p[part.type] = part.value;
  return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute}`;
}

/** Дата без времени «2026-10-31» → «31.10.2026». */
function formatDate(date: string): string {
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}`;
}

function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (roles as readonly string[]).includes(value);
}

function lowerFirst(text: string): string {
  return text ? text.charAt(0).toLocaleLowerCase('ru-RU') + text.slice(1) : text;
}

/** Ключ области из журнала: installation → «вся установка», object:<id> → «объект 0192…». */
function areaKeyText(value: string): string | undefined {
  const match = AREA_KEY.exec(value);
  if (!match) return undefined;
  const kind = match[1] as keyof typeof areaKindLabels;
  const ref = match[2] ? ` ${match[2].slice(0, 8)}` : '';
  return lowerFirst(areaKindLabels[kind]) + ref;
}

type StateLabels = Readonly<Record<string, string>>;

/**
 * Состояние словом по виду записи: замещение «действует», пользователь «активен», фоновая
 * задача «упала».
 */
function stateLabelsFor(operation: string | undefined): StateLabels {
  if (operation?.startsWith('substitution.')) return substitutionStateLabels;
  if (operation?.startsWith('support.')) return supportGrantStateLabels;
  if (operation?.startsWith('job.')) return jobStateLabels;
  return userStateLabels;
}

interface FormatContext {
  readonly timeZone: string;
  readonly states: StateLabels;
  /** Сначала names вызывающего, затем names самой записи. */
  readonly names: readonly (PeopleNames | undefined)[];
}

function formatString(key: string, value: string, ctx: FormatContext): string {
  if (key === 'role' && isRole(value)) return roleInfo[value].inText;
  if (userIdFields.has(key)) {
    for (const names of ctx.names) {
      const name = nameIn(names, value);
      if (name) return name;
    }
  }
  const state = key === 'state' ? ctx.states[value] : undefined;
  if (state) return lowerFirst(state);
  const reason = key === 'reason' ? reasonLabels[value] : undefined;
  if (reason) return reason;
  if (key === 'purpose' && value in purposeLabels) return purposeLabels[value] ?? value;
  if (key === 'jobType' && Object.hasOwn(jobCatalog, value)) {
    return jobCatalog[value as keyof typeof jobCatalog].label;
  }
  if (key === 'jobOperation') return formatOperation(value);
  // 008: секрет в журнале — только последние 4 знака: «…wxyz» (FR-008).
  if (key === 'keyTail') return `…${value}`;
  if (key === 'operation') return lowerFirst(auditOperationLabel(value));
  if (key === 'entityType') return lowerFirst(entityTypeLabel(value));
  if (key === 'result' && (value === 'ok' || value === 'denied')) {
    return lowerFirst(auditResultLabels[value]);
  }
  if (key === 'areas' || key === 'areaKeys') {
    const area = areaKeyText(value);
    if (area) return area;
  }
  if (DATE.test(value)) return formatDate(value);
  if (DATETIME.test(value) && !Number.isNaN(Date.parse(value))) {
    return formatAuditTime(value, ctx.timeZone);
  }
  if (isRole(value)) return roleInfo[value].inText;
  if (isOperation(value)) return lowerFirst(operationLabels[value]);
  return value;
}

function formatItem(key: string, value: unknown, ctx: FormatContext): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'да' : 'нет';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return formatString(key, value, ctx);
  if (typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    // Область: { kind, refId? }.
    if (typeof record.kind === 'string' && record.kind in areaKindLabels) {
      const kindLabel = areaKindLabels[record.kind as keyof typeof areaKindLabels];
      const ref = typeof record.refId === 'string' ? ` ${record.refId.slice(0, 8)}` : '';
      return lowerFirst(kindLabel) + ref;
    }
    // Назначение: { role, areas } → «контролёр (вся установка)».
    if (isRole(record.role)) {
      const areas = record.areas === undefined ? '' : formatList('areas', record.areas, ctx);
      return areas && areas !== '—'
        ? `${roleInfo[record.role].inText} (${areas})`
        : roleInfo[record.role].inText;
    }
    return Object.entries(record)
      .map(([field, item]) => `${lowerFirst(fieldLabel(field))}: ${formatList(field, item, ctx)}`)
      .join(', ');
  }
  return JSON.stringify(value);
}

function formatList(key: string, value: unknown, ctx: FormatContext): string {
  if (Array.isArray(value)) {
    return value.length === 0 ? '—' : value.map((item) => formatItem(key, item, ctx)).join(', ');
  }
  return formatItem(key, value, ctx);
}

/**
 * Значение поля «было» или «стало» для человека; operation — действие записи журнала,
 * names — ФИО по номерам пользователей.
 */
export function formatValue(
  key: string,
  value: unknown,
  timeZone: string,
  operation?: string,
  names?: PeopleNames,
): string {
  return formatList(key, value, { timeZone, states: stateLabelsFor(operation), names: [names] });
}

function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Изменённые поля записи по порядку: сначала поля «стало», затем исчезнувшие. Номера людей —
 * ФИО из names вызывающего или из names самой записи.
 */
export function entryChanges(
  entry: Pick<AuditEntry, 'before' | 'after'> & Partial<Pick<AuditEntry, 'operation' | 'names'>>,
  timeZone: string,
  names?: PeopleNames,
): Change[] {
  const ctx: FormatContext = {
    timeZone,
    states: stateLabelsFor(entry.operation),
    names: [names, entry.names],
  };
  const before = entry.before ?? {};
  const after = entry.after ?? {};
  const keys = [...new Set([...Object.keys(after), ...Object.keys(before)])];
  const changes: Change[] = [];
  for (const key of keys) {
    const was = before[key];
    const now = after[key];
    const label = fieldLabel(key);
    if (Array.isArray(was) && Array.isArray(now)) {
      const added = now.filter((item) => !was.some((old) => sameValue(old, item)));
      const removed = was.filter((old) => !now.some((item) => sameValue(old, item)));
      if (added.length === 0 && removed.length === 0) continue;
      changes.push({
        key,
        label,
        kind: 'list',
        added: added.map((item) => formatItem(key, item, ctx)),
        removed: removed.map((item) => formatItem(key, item, ctx)),
      });
      continue;
    }
    if (sameValue(was, now)) continue;
    if (entry.before && entry.after) {
      changes.push({
        key,
        label,
        kind: 'set',
        before: formatList(key, was, ctx),
        after: formatList(key, now, ctx),
      });
    } else {
      changes.push({
        key,
        label,
        kind: 'value',
        after: formatList(key, entry.after ? now : was, ctx),
      });
    }
  }
  return changes;
}

/**
 * Изменения, где поле (или одно из полей) поменялось: было и стало различаются. Для полосы
 * конфликта (409 REC-001, T124): из всех изменений после If-Match — те, что касаются правимого
 * поля. Чьё назначение и роль есть в каждой записи о назначении — без изменения они не считаются.
 */
export function changesOfField<E extends Pick<AuditEntry, 'before' | 'after'>>(
  entries: readonly E[],
  fields: string | readonly string[],
): E[] {
  const keys = typeof fields === 'string' ? [fields] : fields;
  return entries.filter((entry) =>
    keys.some((key) => {
      const was = entry.before?.[key];
      const now = entry.after?.[key];
      return (was !== undefined || now !== undefined) && !sameValue(was, now);
    }),
  );
}

function changeText(change: Change): string {
  switch (change.kind) {
    case 'set':
      return `${change.before} → ${change.after}`;
    case 'list': {
      const parts: string[] = [];
      if (change.added?.length) parts.push(`добавлено: ${change.added.join(', ')}`);
      if (change.removed?.length) parts.push(`убрано: ${change.removed.join(', ')}`);
      return parts.join(', ');
    }
    case 'value':
      return change.after ?? '—';
  }
}

/**
 * Коротко в строке: роль и списки — без подписи («контролёр → ведущий», «добавлено: …»),
 * остальные поля — с подписью («ФИО: … → …», «В архиве с: — → …»), несколько — через «;».
 */
export function changeSummary(changes: readonly Change[]): string {
  if (changes.length === 0) return '';
  const [only] = changes;
  if (
    changes.length === 1 &&
    only &&
    (only.kind === 'list' || (only.kind === 'set' && only.key === 'role'))
  ) {
    return changeText(only);
  }
  return changes.map((change) => `${change.label}: ${changeText(change)}`).join('; ');
}

/** «ФИО — было «Морозов О. С.», стало «Морозов Олег Сергеевич»» для полосы конфликта. */
export function changeSentence(change: Change): string {
  if (change.kind === 'set') {
    return `${change.label} — было «${change.before}», стало «${change.after}»`;
  }
  return `${change.label} — ${changeText(change)}`;
}

function collectUserIds(value: unknown, key: string, into: Set<string>): void {
  if (typeof value === 'string') {
    if (userIdFields.has(key)) into.add(value);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectUserIds(item, key, into);
    return;
  }
  if (typeof value === 'object' && value !== null) {
    for (const [field, item] of Object.entries(value)) collectUserIds(item, field, into);
  }
}

/**
 * Номера пользователей в «было — стало» записей, чьих ФИО нет в names самой записи: их ФИО
 * вызывающий передаёт в entryChanges.
 */
export function changeUserIds(
  entries: readonly (Pick<AuditEntry, 'before' | 'after'> & Partial<Pick<AuditEntry, 'names'>>)[],
): string[] {
  const ids = new Set<string>();
  for (const entry of entries) {
    const own = new Set<string>();
    collectUserIds(entry.before, '', own);
    collectUserIds(entry.after, '', own);
    for (const id of own) if (!nameIn(entry.names, id)) ids.add(id);
  }
  return [...ids];
}
